import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from '@material-ui/core'
import React, { useEffect, useState } from 'react'

import {
  createPlannerObservation,
  fetchScopedCentres,
  fetchPlannerGradeGroup,
  fetchPlannerObservationPrograms,
  fetchPlannerPlanDetail,
  fetchPlannerPlans,
  fetchPlannerQotQuestions
} from 'api'
import { getLoggedInUserName, showToast } from 'app/helpers'
import { useOpsCentre } from 'app/hooks/useOpsCentre'

import AllObservations from './AllObservations'
import ProgramObservation from './ProgramObservation'

import { PLANNER_WEEKDAY_NAMES, mondayOf } from '../constants'

const items = (d) =>
  d && Array.isArray(d.results) ? d.results : Array.isArray(d) ? d : []

const addDays = (iso, n) => {
  const d = new Date(iso)
  d.setDate(d.getDate() + n)
  return d.toISOString().slice(0, 10)
}

const blankDraft = () => ({
  teacher_name: '',
  observation_date: new Date().toISOString().slice(0, 10),
  observer_label: '',
  env_setup: {},
  facilitation: {},
  did_well: '',
  to_improve: ''
})

const YesNo = ({ value, onChange }) => (
  <Box display="flex" style={{ gap: 4 }}>
    {['yes', 'no'].map((v) => (
      <Button
        key={v}
        size="small"
        variant={value === v ? 'contained' : 'outlined'}
        color={value === v ? 'primary' : 'default'}
        onClick={() => onChange(v)}
        style={{ minWidth: 44 }}
      >
        {v === 'yes' ? 'Yes' : 'No'}
      </Button>
    ))}
  </Box>
)

/** Reviewer / admin: browse a centre's plans week-by-week and fill QOT observations. */
const Plans = ({ initialCentre = '' }) => {
  const [centres, setCentres] = useState([])
  const [grades, setGrades] = useState([])
  // Shared by every Centre Operations page and kept across refreshes. Plans
  // need one centre, so a saved "All centres" opens on none picked.
  const [centreId, setCentreId, pickCentre] = useOpsCentre({
    fallback: initialCentre,
    allowAll: false
  })
  const [gradeId, setGradeId] = useState('')
  const [programs, setPrograms] = useState([]) // org-level observation programs
  const [programId, setProgramId] = useState('')
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()))
  const [cards, setCards] = useState([])
  const [loadingCards, setLoadingCards] = useState(false)

  const [section, setSection] = useState(null) // selected section card
  const [cells, setCells] = useState([])
  const [loadingPlan, setLoadingPlan] = useState(false)

  const [lesson, setLesson] = useState(null) // selected cell
  const [draft, setDraft] = useState(blankDraft())
  const [saving, setSaving] = useState(false)
  const [observationTab, setObservationTab] = useState('fill') // 'fill' | 'all'
  // The selected grade's editable QOT questions (drives the preschool form).
  const [qotSetup, setQotSetup] = useState([])
  const [qotFac, setQotFac] = useState([])

  useEffect(() => {
    fetchScopedCentres()
      .then((c) => {
        const list = items(c)
        setCentres(list)
        // A saved centre this person is not mapped to starts on none picked.
        setCentreId((prev) =>
          !prev || list.some((x) => x.uuid === prev) ? prev : ''
        )
      })
      .catch(() => showToast('Failed to load centres', 'danger'))
    fetchPlannerGradeGroup({})
      .then((g) => setGrades(items(g)))
      .catch(() => {})
    fetchPlannerObservationPrograms({ queries: { active: 'true' } })
      .then((d) => setPrograms(items(d)))
      .catch(() => {})
  }, [])

  const loadCards = async () => {
    if (programId) {
      setCards([])
      return
    } // program mode uses the questionnaire, not cards
    if (!centreId) {
      setCards([])
      return
    }
    setLoadingCards(true)
    try {
      const q = { centre: centreId, week_start: weekStart }
      if (gradeId) q.grade_group = gradeId
      const data = await fetchPlannerPlans({ queries: q })
      setCards((data && data.sections) || [])
    } catch (e) {
      showToast('Failed to load plans', 'danger')
    }
    setLoadingCards(false)
  }

  useEffect(() => {
    loadCards()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centreId, gradeId, weekStart, programId])

  const openSection = async (card) => {
    setSection(card)
    setLoadingPlan(true)
    // This grade's editable QOT questions (for the observation form).
    fetchPlannerQotQuestions({
      queries: { grade_group: card.section.grade_group, active: 'true' }
    })
      .then((d) => {
        const qs = items(d)
        setQotSetup(qs.filter((q) => q.section === 'setup'))
        setQotFac(qs.filter((q) => q.section === 'facilitation'))
      })
      .catch(() => {
        setQotSetup([])
        setQotFac([])
      })
    try {
      const data = await fetchPlannerPlanDetail({
        queries: {
          centre: card.section.centre,
          grade: card.section.grade_group,
          class_key: card.section.class_key,
          week_start: weekStart
        }
      })
      setCells((data && data.cells) || [])
    } catch (e) {
      showToast('Failed to load plan', 'danger')
    }
    setLoadingPlan(false)
  }

  const reloadPlan = async () => {
    if (!section) return
    const data = await fetchPlannerPlanDetail({
      queries: {
        centre: section.section.centre,
        grade: section.section.grade_group,
        class_key: section.section.class_key,
        week_start: weekStart
      }
    })
    setCells((data && data.cells) || [])
  }

  // The open class's teachers, for the observation's Teacher picker.
  const classTeachers = [
    ...new Set(
      ((section && section.section.teacher_names) || []).filter(
        (n) => n && n !== 'Teacher'
      )
    )
  ]

  const openLesson = (cell) => {
    setLesson(cell)
    // Observer is the logged-in Alfred user — auto-filled, not typed. A class
    // with one teacher starts with her picked.
    setDraft({
      ...blankDraft(),
      observer_label: getLoggedInUserName() || '',
      teacher_name: classTeachers.length === 1 ? classTeachers[0] : ''
    })
  }

  const submitObservation = async () => {
    setSaving(true)
    try {
      await createPlannerObservation({ plan_cell: lesson.uuid, ...draft })
      showToast('Observation saved', 'success')
      await reloadPlan()
      // refresh the open lesson's observations from the reloaded cells
      setLesson(null)
    } catch (e) {
      showToast('Failed to save observation', 'danger')
    }
    setSaving(false)
  }

  // ----- overview -----
  if (!section) {
    const gradeMeta = {}
    grades.forEach((g) => {
      gradeMeta[g.name] = { emoji: g.emoji || '', color: g.color || '#c9a14a' }
    })
    return (
      <Box p={2}>
        <Typography variant="h5" gutterBottom>
          Observations
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Pick a centre and a programme. Preschool grades open the weekly lesson
          plans (open a lesson to write a QOT); other programmes open a teacher
          observation form. Teachers don&apos;t see observations.
        </Typography>

        <Box display="flex" style={{ gap: 8 }} mt={2} mb={1}>
          <Button
            variant={observationTab === 'fill' ? 'contained' : 'outlined'}
            color={observationTab === 'fill' ? 'primary' : 'default'}
            onClick={() => setObservationTab('fill')}
          >
            Fill observation
          </Button>
          <Button
            variant={observationTab === 'all' ? 'contained' : 'outlined'}
            color={observationTab === 'all' ? 'primary' : 'default'}
            onClick={() => setObservationTab('all')}
          >
            All observations
          </Button>
        </Box>

        {observationTab === 'all' ? (
          <AllObservations />
        ) : (
          <>
            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              style={{ gap: 12 }}
              mt={2}
              mb={2}
            >
              <FormControl
                variant="outlined"
                size="small"
                style={{ minWidth: 180 }}
              >
                <InputLabel>Centre</InputLabel>
                <Select
                  label="Centre"
                  value={centreId}
                  onChange={(e) => pickCentre(e.target.value)}
                >
                  {centres.map((c) => (
                    <MenuItem key={c.uuid} value={c.uuid}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                variant="outlined"
                size="small"
                style={{ minWidth: 210 }}
              >
                <InputLabel>Program</InputLabel>
                <Select
                  label="Program"
                  value={programId ? `p:${programId}` : `g:${gradeId}`}
                  onChange={(e) => {
                    const v = e.target.value
                    setSection(null)
                    setLesson(null)
                    if (v.indexOf('p:') === 0) {
                      setProgramId(v.slice(2))
                      setGradeId('')
                    } else {
                      setGradeId(v.slice(2))
                      setProgramId('')
                      setCards([])
                    }
                  }}
                >
                  <ListSubheader>Preschool</ListSubheader>
                  <MenuItem value="g:">
                    <em>All preschool grades</em>
                  </MenuItem>
                  {grades.map((g) => (
                    <MenuItem key={g.uuid} value={`g:${g.uuid}`}>
                      {`${g.emoji || ''} ${g.name}`}
                    </MenuItem>
                  ))}
                  {programs.length > 0 && (
                    <ListSubheader>Programs</ListSubheader>
                  )}
                  {programs.map((p) => (
                    <MenuItem key={p.uuid} value={`p:${p.uuid}`}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {!programId && (
                <>
                  <Button
                    size="small"
                    onClick={() => setWeekStart(addDays(weekStart, -7))}
                  >
                    ◀ Prev
                  </Button>
                  <TextField
                    type="date"
                    size="small"
                    label="Week of"
                    InputLabelProps={{ shrink: true }}
                    value={weekStart}
                    onChange={(e) => {
                      const m = mondayOf(e.target.value)
                      if (m) setWeekStart(m)
                    }}
                  />
                  <Button
                    size="small"
                    onClick={() => setWeekStart(addDays(weekStart, 7))}
                  >
                    Next ▶
                  </Button>
                </>
              )}
            </Box>

            {programId ? (
              <ProgramObservation
                centreId={centreId}
                program={programs.find((p) => p.uuid === programId)}
              />
            ) : !centreId ? (
              <Typography variant="body2" color="textSecondary">
                Pick a centre to see its grades.
              </Typography>
            ) : loadingCards ? (
              <CircularProgress />
            ) : (
              <Box display="flex" flexWrap="wrap" style={{ gap: 14 }}>
                {cards.map((card) => {
                  const meta = gradeMeta[card.section.grade_group_name] || {
                    emoji: '',
                    color: '#c9a14a'
                  }
                  const required = card.required_count || 0
                  const pct = required
                    ? Math.min(
                        100,
                        Math.round((card.planned_count / required) * 100)
                      )
                    : 0
                  const done = required > 0 && card.planned_count >= required
                  return (
                    <Paper
                      key={card.section.uuid}
                      elevation={0}
                      style={{
                        width: 264,
                        cursor: 'pointer',
                        overflow: 'hidden',
                        borderRadius: 12,
                        border: '1px solid #e8e3db',
                        transition: 'box-shadow .15s ease, transform .15s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow =
                          '0 6px 18px rgba(0,0,0,0.10)'
                        e.currentTarget.style.transform = 'translateY(-2px)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = 'none'
                        e.currentTarget.style.transform = 'none'
                      }}
                      onClick={() => openSection(card)}
                    >
                      <Box style={{ height: 6, background: meta.color }} />
                      <Box p={1.75}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                        >
                          <Box pr={1}>
                            <Typography
                              variant="subtitle1"
                              style={{ fontWeight: 600, lineHeight: 1.2 }}
                            >
                              {meta.emoji ? `${meta.emoji} ` : ''}
                              {card.section.grade_group_name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {card.section.centre_name}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            label={
                              card.status === 'submitted'
                                ? 'Submitted'
                                : 'Draft'
                            }
                            style={
                              card.status === 'submitted'
                                ? {
                                    backgroundColor: '#2d9d63',
                                    color: '#fff',
                                    fontWeight: 600
                                  }
                                : {
                                    backgroundColor: '#efe9df',
                                    color: '#7a6f65',
                                    fontWeight: 600
                                  }
                            }
                          />
                        </Box>
                        <Box mt={1.75}>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={0.5}
                          >
                            <Typography variant="caption" color="textSecondary">
                              Sessions planned
                            </Typography>
                            <Typography
                              variant="caption"
                              style={{
                                fontWeight: 700,
                                color: done ? '#2d9d63' : '#3a322b'
                              }}
                            >
                              {card.planned_count}/{required}
                            </Typography>
                          </Box>
                          <Box
                            style={{
                              height: 7,
                              borderRadius: 4,
                              background: '#ece7df',
                              overflow: 'hidden'
                            }}
                          >
                            <Box
                              style={{
                                height: '100%',
                                width: `${pct}%`,
                                background: done ? '#2d9d63' : meta.color,
                                borderRadius: 4,
                                transition: 'width .3s ease'
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  )
                })}
                {!cards.length && (
                  <Box py={3} px={2} width="100%" textAlign="center">
                    <Typography variant="body2" color="textSecondary">
                      No grades set up for this centre yet.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    )
  }

  // ----- section detail (list of planned lessons) -----
  const byDay = PLANNER_WEEKDAY_NAMES.map((name, i) => ({
    name,
    cells: cells.filter((c) => c.weekday === i)
  }))
  // Every QOT question for this grade must be answered before saving.
  const qotDone =
    qotSetup.every((p) => draft.env_setup[p.key]) &&
    qotFac.every((p) => draft.facilitation[p.key])

  return (
    <Box p={2}>
      <Button
        size="small"
        onClick={() => {
          setSection(null)
          setCells([])
        }}
      >
        ◀ Back to overview
      </Button>
      <Typography variant="h5" style={{ marginTop: 8 }}>
        {section.section.grade_group_name}
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {section.section.centre_name} · week of {weekStart}
      </Typography>

      {loadingPlan ? (
        <CircularProgress />
      ) : (
        <Box mt={1}>
          {byDay.map((day) => (
            <Box key={day.name} mb={2}>
              <Typography
                variant="overline"
                style={{ color: '#9a8f82', letterSpacing: 1, fontWeight: 600 }}
              >
                {day.name}
              </Typography>
              {!day.cells.length && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ fontStyle: 'italic' }}
                >
                  Nothing planned
                </Typography>
              )}
              {day.cells.map((cell) => {
                const obs = (cell.observations || []).length
                return (
                  <Paper
                    key={cell.uuid}
                    elevation={0}
                    style={{
                      padding: '10px 12px',
                      marginTop: 6,
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      borderRadius: 10,
                      border: '1px solid #e8e3db',
                      transition: 'box-shadow .15s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow =
                        '0 4px 12px rgba(0,0,0,0.08)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                    onClick={() => openLesson(cell)}
                  >
                    <Box style={{ flexGrow: 1 }}>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {cell.title ||
                          (cell.is_multi
                            ? '(multi-activity slot)'
                            : '(activity)')}
                      </Typography>
                      <Typography
                        variant="caption"
                        style={{
                          color: cell.is_custom ? '#b07b1e' : '#2d6a4f'
                        }}
                      >
                        {cell.is_custom
                          ? '✎ Your own activity'
                          : '📚 From repository'}
                      </Typography>
                    </Box>
                    {obs > 0 && (
                      <Chip
                        size="small"
                        label={`📝 ${obs}`}
                        style={{
                          backgroundColor: '#eef4ff',
                          color: '#2a5db0',
                          fontWeight: 600
                        }}
                      />
                    )}
                  </Paper>
                )
              })}
            </Box>
          ))}
          {!cells.length && (
            <Box py={3} textAlign="center">
              <Typography variant="body2" color="textSecondary">
                Nothing planned this week yet.
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Lesson detail + QOT */}
      <Dialog
        open={!!lesson}
        onClose={() => setLesson(null)}
        fullWidth
        maxWidth="sm"
        scroll="paper"
      >
        <DialogTitle>Lesson detail</DialogTitle>
        <DialogContent dividers>
          {lesson && (
            <Box>
              <Typography variant="h6">
                {lesson.title || '(activity)'}
              </Typography>
              {(lesson.outcomes || []).length > 0 && (
                <>
                  <Typography variant="subtitle2" style={{ marginTop: 8 }}>
                    Learning outcomes
                  </Typography>
                  <ul style={{ margin: 0 }}>
                    {lesson.outcomes.map((o, i) => (
                      <li key={i}>
                        <Typography variant="body2">{o}</Typography>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {(lesson.steps || []).length > 0 && (
                <>
                  <Typography variant="subtitle2" style={{ marginTop: 8 }}>
                    Steps
                  </Typography>
                  <ul style={{ margin: 0 }}>
                    {lesson.steps.map((s, i) => (
                      <li key={i}>
                        <Typography variant="body2">
                          <strong>{s.label}:</strong> {s.text}
                        </Typography>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {lesson.visual_evolution && (
                <>
                  <Typography variant="subtitle2" style={{ marginTop: 8 }}>
                    Visual evolution
                  </Typography>
                  <Typography variant="body2">
                    {lesson.visual_evolution}
                  </Typography>
                </>
              )}
              {(lesson.materials || []).length > 0 && (
                <>
                  <Typography variant="subtitle2" style={{ marginTop: 8 }}>
                    Materials
                  </Typography>
                  <ul style={{ margin: 0 }}>
                    {lesson.materials.map((m, i) => (
                      <li key={i}>
                        <Typography variant="body2">{m}</Typography>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              <Divider style={{ margin: '16px 0' }} />
              <Typography variant="subtitle1">📝 Class Observations</Typography>
              <Typography variant="caption" color="textSecondary">
                Only reviewers and admin see or write here — the teacher does
                not.
              </Typography>
              {(lesson.observations || []).length === 0 && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ marginTop: 6 }}
                >
                  No observations yet.
                </Typography>
              )}
              {(lesson.observations || []).map((o) => (
                <Paper
                  key={o.uuid}
                  elevation={0}
                  style={{
                    padding: 10,
                    marginTop: 6,
                    borderRadius: 8,
                    background: '#f7f4ef',
                    border: '1px solid #ece7df'
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    {o.observer_label || 'Observer'} ·{' '}
                    {o.observation_date || ''} · teacher:{' '}
                    {o.teacher_name || '—'}
                  </Typography>
                  {o.did_well && (
                    <Typography variant="body2">
                      <strong>Did well:</strong> {o.did_well}
                    </Typography>
                  )}
                  {o.to_improve && (
                    <Typography variant="body2">
                      <strong>To improve:</strong> {o.to_improve}
                    </Typography>
                  )}
                </Paper>
              ))}

              <Divider style={{ margin: '16px 0' }} />
              <Typography variant="subtitle2" gutterBottom>
                Add an observation
              </Typography>
              <Box display="flex" style={{ gap: 12 }} mb={1} flexWrap="wrap">
                {/* Picked from the class's teachers, so the name always matches
                    her Alfred account and the Teacher Scorecard credits the
                    right person (Shruti, 23 Oct); typed only if the class has
                    none listed. */}
                {classTeachers.length ? (
                  <TextField
                    select
                    size="small"
                    label="Teacher"
                    value={draft.teacher_name}
                    onChange={(e) => {
                      const v = e.target.value
                      setDraft((d) => ({ ...d, teacher_name: v }))
                    }}
                    style={{ minWidth: 200 }}
                  >
                    {classTeachers.map((n) => (
                      <MenuItem key={n} value={n}>
                        {n}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <TextField
                    size="small"
                    label="Teacher's name"
                    value={draft.teacher_name}
                    onChange={(e) => {
                      const v = e.target.value
                      setDraft((d) => ({ ...d, teacher_name: v }))
                    }}
                  />
                )}
                <TextField
                  size="small"
                  type="date"
                  label="Date"
                  InputLabelProps={{ shrink: true }}
                  value={draft.observation_date}
                  onChange={(e) => {
                    const v = e.target.value
                    setDraft((d) => ({ ...d, observation_date: v }))
                  }}
                />
                <TextField
                  size="small"
                  label="Observer (you)"
                  value={draft.observer_label}
                  InputProps={{ readOnly: true }}
                  helperText="Auto-filled from your login"
                />
              </Box>

              <Typography variant="subtitle2" style={{ marginTop: 8 }}>
                Environment &amp; Setup
              </Typography>
              {!qotSetup.length && !qotFac.length && (
                <Typography variant="caption" color="textSecondary">
                  No QOT questions set up for this grade yet.
                </Typography>
              )}
              {qotSetup.map((p) => (
                <Box
                  key={p.key}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mt={0.5}
                >
                  <Box pr={2}>
                    <Typography variant="body2">{p.title}</Typography>
                    {p.description && (
                      <Typography variant="caption" color="textSecondary">
                        {p.description}
                      </Typography>
                    )}
                  </Box>
                  <YesNo
                    value={draft.env_setup[p.key]}
                    onChange={(v) =>
                      setDraft((d) => ({
                        ...d,
                        env_setup: { ...d.env_setup, [p.key]: v }
                      }))
                    }
                  />
                </Box>
              ))}

              <Typography variant="subtitle2" style={{ marginTop: 12 }}>
                Facilitation
              </Typography>
              {qotFac.map((p) => (
                <Box
                  key={p.key}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mt={0.5}
                >
                  <Box pr={2}>
                    <Typography variant="body2">{p.title}</Typography>
                    {p.description && (
                      <Typography variant="caption" color="textSecondary">
                        {p.description}
                      </Typography>
                    )}
                  </Box>
                  <YesNo
                    value={draft.facilitation[p.key]}
                    onChange={(v) =>
                      setDraft((d) => ({
                        ...d,
                        facilitation: { ...d.facilitation, [p.key]: v }
                      }))
                    }
                  />
                </Box>
              ))}

              <TextField
                fullWidth
                multiline
                minRows={2}
                style={{ marginTop: 12 }}
                label="What did the teacher do very well?"
                value={draft.did_well}
                onChange={(e) => {
                  const v = e.target.value
                  setDraft((d) => ({ ...d, did_well: v }))
                }}
              />
              <TextField
                fullWidth
                multiline
                minRows={2}
                style={{ marginTop: 12 }}
                label="What does the teacher need to improve upon?"
                value={draft.to_improve}
                onChange={(e) => {
                  const v = e.target.value
                  setDraft((d) => ({ ...d, to_improve: v }))
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!qotDone && (
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ marginRight: 'auto', paddingLeft: 8 }}
            >
              Answer all questions to save.
            </Typography>
          )}
          <Button onClick={() => setLesson(null)}>Close</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={submitObservation}
            disabled={saving || !qotDone}
          >
            {saving ? 'Saving…' : 'Save observation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Plans
